import EventCard from "./event-card";
import { cn } from "@/lib/utils";
import { useRouter } from "next/router";
import { DateTime } from "luxon";
import { Button } from "../ui/button";
import Link from "next/link";
import { useTranslations } from "next-intl";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import MovieEventCard from "./movie-event-card";

function groupEventsByDate(events: any) {
  const groupedEvents: any = {};

  events.forEach((event: any) => {
    const startDate = new Date(event.startDate).toISOString().split("T")[0];

    if (!groupedEvents[startDate]) {
      groupedEvents[startDate] = [];
    }

    groupedEvents[startDate].push(event);
  });

  const groupedArray = Object.keys(groupedEvents).map((date) => ({
    date,
    events: groupedEvents[date],
  }));

  return groupedArray;
}

function filterEvents(events: any, isPast: boolean) {
  const now = DateTime.now().toISODate();
  return events.filter((event: any) =>
    isPast ? event.endDate < now : event.endDate >= now,
  );
}

interface EventListProps {
  events: any[];
  className?: string;
  viewMore?: boolean;
  enableDateFiltering?: boolean;
  category?: string;
}
export default function EventList({
  events,
  className,
  viewMore = false,
  enableDateFiltering = false,
  category,
}: EventListProps) {
  const { locale } = useRouter();
  const t = useTranslations("AllEventsPage");

  //  const isCinemaFilterActive = selectedCategories.includes("Cinema");

  // For cinema events, we'll show them without the timeline view
  if (category === "Cinema") {
    // Filter events that have projections

    return (
      <div className={cn("w-full", className)}>
        <div className="flex flex-col gap-4">
          {events.map((event: any, idx: number) => (
            <MovieEventCard key={idx} event={event} />
          ))}
        </div>
        {viewMore && (
          <div className="flex justify-center w-full mt-6">
            <Button
              className="transition-opacity rounded-full opacity-80 hover:opacity-100"
              size={"sm"}
              asChild
            >
              <Link href="/all">{t("load-more")}</Link>
            </Button>
          </div>
        )}
      </div>
    );
  }

  // For regular events, keep the timeline view
  const currentAndFutureEvents = filterEvents(events, false);
  const pastEvents = filterEvents(events, true);

  const days = groupEventsByDate(currentAndFutureEvents);
  const pastDays = groupEventsByDate(pastEvents);

  if (!enableDateFiltering) {
    return (
      <ol
        className={cn(
          "w-full relative border-l border-opacity-80 border-dashed border-gray-300 dark:border-gray-600",
          className,
        )}
      >
        {days.map((day: any, idx: number) => (
          <li className="mb-10 ms-4" key={idx}>
            <div className="sticky z-20 flex flex-row items-center gap-3 top-14">
              <div className="-ml-6 w-3.5 h-3.5 rounded-full start-[-7px] border-2 border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-900"></div>
              <time className="px-3 py-1 mb-1 space-x-1 text-sm font-normal leading-none lowercase border rounded-full backdrop-blur bg-background/20 border-primary/5">
                <div
                  className="inline-block text-base font-medium"
                  suppressHydrationWarning
                >
                  {DateTime.fromISO(day.date, { zone: "utc" }).toFormat(
                    "d LLL",
                    {
                      locale,
                    },
                  )}
                </div>
                <div
                  className="inline-block text-base font-normal text-gray-400 dark:text-gray-500 opacity-60"
                  suppressHydrationWarning
                >
                  {DateTime.fromISO(day.date, { zone: "utc" }).toFormat(
                    "EEEE",
                    {
                      locale,
                    },
                  )}
                </div>
              </time>
            </div>
            <div className="flex flex-col gap-4 py-3">
              {day.events.map((event: any, idx: number) => (
                <EventCard key={idx} event={event} />
              ))}
            </div>
          </li>
        ))}
        {viewMore && (
          <div className="flex justify-center w-full">
            <Button
              className="transition-opacity rounded-full opacity-80 hover:opacity-100"
              size={"sm"}
              asChild
            >
              <Link href="/all">{t("load-more")}</Link>
            </Button>
          </div>
        )}
      </ol>
    );
  }

  return (
    <Tabs defaultValue="current" className="w-full max-w-xl my-2">
      <TabsList className="flex justify-end items-end w-fit">
        <TabsTrigger value="current">Current & Future Events</TabsTrigger>
        <TabsTrigger value="past">Past Events</TabsTrigger>
      </TabsList>
      <TabsContent value="current">
        <ol
          className={cn(
            "w-full relative border-l border-opacity-80 border-dashed border-gray-300 dark:border-gray-600",
            className,
          )}
        >
          {days.map((day: any, idx: number) => (
            <li className="mb-10 ms-4" key={idx}>
              <div className="sticky z-20 flex flex-row items-center gap-3 top-14">
                <div className="-ml-6 w-3.5 h-3.5 rounded-full start-[-7px] border-2 border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-900"></div>
                <time className="px-3 py-1 mb-1 space-x-1 text-sm font-normal leading-none lowercase border rounded-full backdrop-blur bg-background/20 border-primary/5">
                  <div
                    className="inline-block text-base font-medium"
                    suppressHydrationWarning
                  >
                    {DateTime.fromISO(day.date, { zone: "utc" }).toFormat(
                      "d LLL",
                      {
                        locale,
                      },
                    )}
                  </div>
                  <div
                    className="inline-block text-base font-normal text-gray-400 dark:text-gray-500 opacity-60"
                    suppressHydrationWarning
                  >
                    {DateTime.fromISO(day.date, { zone: "utc" }).toFormat(
                      "EEEE",
                      {
                        locale,
                      },
                    )}
                  </div>
                </time>
              </div>
              <div className="flex flex-col gap-4 py-3">
                {day.events.map((event: any, idx: number) => (
                  <EventCard key={idx} event={event} />
                ))}
              </div>
            </li>
          ))}
          {viewMore && (
            <div className="flex justify-center w-full">
              <Button
                className="transition-opacity rounded-full opacity-80 hover:opacity-100"
                size={"sm"}
                asChild
              >
                <Link href="/all">{t("load-more")}</Link>
              </Button>
            </div>
          )}
        </ol>
      </TabsContent>
      <TabsContent value="past">
        <ol
          className={cn(
            "w-full relative border-l border-opacity-80 border-dashed border-gray-300 dark:border-gray-600",
            className,
          )}
        >
          {pastDays.map((day: any, idx: number) => (
            <li className="mb-10 ms-4" key={idx}>
              <div className="sticky z-20 flex flex-row items-center gap-3 top-14">
                <div className="-ml-6 w-3.5 h-3.5 rounded-full start-[-7px] border-2 border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-900"></div>
                <time className="px-3 py-1 mb-1 space-x-1 text-sm font-normal leading-none lowercase border rounded-full backdrop-blur bg-background/20 border-primary/5">
                  <div
                    className="inline-block text-base font-medium"
                    suppressHydrationWarning
                  >
                    {DateTime.fromISO(day.date, { zone: "utc" }).toFormat(
                      "d LLL",
                      {
                        locale,
                      },
                    )}
                  </div>
                  <div
                    className="inline-block text-base font-normal text-gray-400 dark:text-gray-500 opacity-60"
                    suppressHydrationWarning
                  >
                    {DateTime.fromISO(day.date, { zone: "utc" }).toFormat(
                      "EEEE",
                      {
                        locale,
                      },
                    )}
                  </div>
                </time>
              </div>
              <div className="flex flex-col gap-4 py-3">
                {day.events.map((event: any, idx: number) => (
                  <EventCard key={idx} event={event} />
                ))}
              </div>
            </li>
          ))}
          {viewMore && (
            <div className="flex justify-center w-full">
              <Button
                className="transition-opacity rounded-full opacity-80 hover:opacity-100"
                size={"sm"}
                asChild
              >
                <Link href="/all">{t("load-more")}</Link>
              </Button>
            </div>
          )}
        </ol>
      </TabsContent>
    </Tabs>
  );
}
